// Third party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps, Redirect, generatePath } from 'react-router-dom';
import moment from 'moment';
import { LocationDescriptorObject } from 'history';
import classNames from 'classnames';

// Namespaces
import * as ProposalNS from '@/modules/proposal/Proposal';

// Constants and types
import { COMPARISON_ITEM_TYPES, COMPARISON_NAV_PATHS, NAV_PATHS } from '@/utils/constants';
import { PROPOSAL_PAPER_FORMAT } from '@/modules/proposal/utils/constants';
import Dictionary from '@/types/dictionary';
import Locale from '@/types/locale';
import Comparison from '@/types/comparison';
import CorrelationMetric from '@/types/correlation-metric';
import ComparisonItem from '@/types/comparison-item';
import RiskProfileItem from '@/types/risk-profile-item';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import Nullable from '@/types/nullable';
import AdvisorContext from '@/types/advisor-context';
import QuestionnaireAnswers from '@/types/questionnaire-answers';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import ProposalTextAreaData from '@/types/proposal-text-area-data';
import Portfolio from '@/types/portfolio';
import Contact from '@/types/contact';
import GenericContact from '@/types/generic-contact';
import ComparisonGroupProduct from '@/types/comparison-group-product';
import MockableUserInfos from '@/types/mockable-user-infos';
import User from '@/types/user';
import AppMode from '@/types/app-mode';
import NavigationItem from '@/types/navigation-item';
import ComparisonToolbarOptions from '@/types/comparison-toolbar-options';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import ProposalPdfMetadata from '@/types/proposal-pdf-metadata';
import ThemeMode from '@/types/theme-mode';
import { Status as ProposalStatus } from '@/types/status';
import OmniRiskProfilesQuestionnaire from '@/types/risk-profiles/omni/omni-risk-profiles-questionnaire';
import { UUID } from '@/types/global';
import { ApiResponse, HttpStatusCode } from '@/utils/api';
import HistoricalScenario from '@/types/historical-scenario';

// Utils
import { formatMessage, FormattedMessage, formatLocalizedString, formatMessageWithLocale, LOCALE } from '@/utils/i18n';
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import { downloadFile } from '@/utils/file';
import { deepClone, uniqueArray } from '@/utils/objects';
import { PORTFOLIO_TYPES } from '@/utils/portfolios';
import { getComparisonContactName, comparisonIsReadOnly, COMPARISON_TYPE } from '@/utils/comparison';
import {
  getMaxNumberOfItems,
  getDefaultOrientation,
  getProposalTemplateSectionKeys,
  getProposalTemplateDivisionKeys,
  filterItemsByItemSelectionFilters,
  updateGICItemNames,
  itemSelectorFiltersContainsGIC,
  getSidebarNavigationTabs,
} from '@/modules/proposal/ProposalHelpers';
import localStorageUtils from '@/utils/local-storage';
import { PERMISSIONS, userHasPermissionEnabled, userHasPermission } from '@/utils/permissions';
import { DropItemPosition } from '@/utils/drag-and-drop';
import notification from '@/utils/notification';

// Components
import { AnnouncementBanner } from '../announcements';
import DelegateBanner from '../auth/DelegateBanner';
import ProposalBuilderComponent from '@/modules/proposal/components/preview/ProposalBuilderComponent';
import { ProposalSidebarOptions } from '@/modules/proposal/components/proposal-sidebar/ProposalSidebar';
import { PrimaryNavigation } from '../../components/navigation';
import { DiscardComparisonModal, DownloadProposalFormModal, WarningModal } from '../../components/modals';
import StatusFeedbackModal from '../../components/modals/StatusFeedbackModal';
import AdvisorBrandingForm from './AdvisorBrandingForm';
import DefaultHeader from './top-navigation/DefaultHeader';
import LinkTopHeader from './top-navigation/LinkTopHeader';
import MobileHeader from './top-navigation/MobileHeader';
import DownloadPDFButton from '@/modules/proposal/components/proposal-toolbox/DownloadPDFButton';

// Actions
import { ReduxState } from '@/redux/modules/types';
import store from '@/redux/store';
import { resetPortfolio, savePortfolio } from '@/redux/modules/edit-portfolio';
import {
  setProposalTemplate,
  setProposalTemplateSections,
  setProposalTemplateDivisions,
  setProposalItems,
  setProposalItemsCorrelation,
  setProposalGifItems,
  setProposalItemFlags,
  toggleGifHideBaseScenario,
  setProposalTemplateLocale,
  setProposalTemplateTheme,
  setProposalRiskProfile,
  setToolbarOption,
  downloadProposalPDF,
  setProposalContact,
  setProposalDate,
  resetProposal,
  setProposalTemplateOptions,
  setProposalStyleOptions,
  setTextAreaData,
  setSelectedView,
  cancelPDFDownload,
  loadProposalTemplates,
  changeProposalItemsOrder,
  changeProposalContentOrder,
  setProposalTemplateHiddenLayouts,
} from '@/redux/modules/proposal-v2';
import {
  closeComparison,
  toggleShortHistory,
  addPortfoliosToComparison,
  saveComparison,
  setComparisonName,
  setComparisonContact,
  changeItemId,
  rerunComparison,
  changeComparisonItemsOrder,
  updateComparisonGroup,
  replacePortfolioInComparison,
  setComparisonItemFlags,
  setProposalLocale,
  calculateHistoricalScenarios,
} from '@/redux/modules/comparison';
import { ProposalTemplateOptions } from '@/redux/modules/types/proposal-v2';
import { consumeLimit } from '@/redux/modules/rate-limits';
import { RateLimitType } from '@/redux/modules/types/rate-limits';
import { getKycQuestionnaires } from '@/redux/modules/kyc-questionnaire';
import { loadOmniRiskProfilesQuestionnaire } from '@/redux/modules/omni-risk-profiles';
import { planningPlanAccountsApi } from '@/redux/api/planning';
import { convertDateToString, convertStringToDate } from '@/utils/dates';

type StateProps = {
  locale: Locale;
  calculatedHistoricalScenarios: any[];
  comparison?: Comparison;
  comparisonCorrelations: CorrelationMetric;
  proposalItems: ComparisonItem[];
  proposalItemsCorrelation?: CorrelationMetric;
  proposalGifItems: BMOGIFItem[];
  riskProfile?: RiskProfileItem;
  toolbarOptions: Dictionary<ProposalToolbarOption>;
  proposalDate?: Date;
  proposalContact?: ProposalNS.ProposalContact;
  proposalTemplateOptions: ProposalTemplateOptions;
  templates: ProposalNS.BaseProposalTemplate[];
  gifHideBaseScenario?: boolean;
  context?: Nullable<AdvisorContext>;
  rationaleAnswers?: QuestionnaireAnswers;
  questionnaires: KycQuestionnaire[];
  omniRiskProfilesQuestionnaire?: OmniRiskProfilesQuestionnaire;
  isEditVisible: boolean;
  isEditPinned: boolean;
  canPortfolioSave: boolean;
  isSaving: boolean;
  tab?: string;
  originPath?: Location;
  theme?: ThemeMode;
};

type DispatchProps = {
  calculateHistoricalScenarios: (historicalScenarios: HistoricalScenario[], togetherMode: boolean) => Promise<any>;
  setProposalTemplate: (template: ProposalNS.BaseProposalTemplate, setDefault?: boolean) => void;
  setProposalTemplateHiddenLayouts: (templateKey: string, hiddenLayouts: string[]) => void;
  setProposalTemplateSections: (templateKey: string, sections: string[]) => void;
  setProposalTemplateDivisions: (templateKey: string, divisions: string[]) => void;
  setProposalItems: (items: ComparisonItem[]) => void;
  setProposalItemsCorrelation: (correlation: CorrelationMetric) => void;
  setProposalGifItems: (items: BMOGIFItem[]) => void;
  setProposalItemFlags: (itemId: string, flags: string[]) => void;
  setComparisonItemFlags: (itemId: string, flags: string[]) => void;
  toggleGifHideBaseScenario: (value: boolean) => void;
  setProposalRiskProfile: (riskProfile: RiskProfileItem) => void;
  setToolbarOption: (key: string, toolbarOption: ProposalToolbarOption) => void;
  setProposalTemplateLocale: (templateKey: string, locale: Locale) => void;
  setProposalTemplateTheme: (templateKey: string, theme: string) => void;
  downloadProposalPDF: (
    title: string,
    brandingInfo: Nullable<Dictionary<string | undefined>>,
    proposalPdfMetadata?: ProposalPdfMetadata,
  ) => Promise<ApiResponse<Blob | null>>;
  closeComparison: (comparison: Comparison) => void;
  setProposalContact: (contactName: ProposalNS.ProposalContact) => void;
  setProposalDate: (date: Date) => void;
  resetProposal: () => void;
  setProposalTemplateOptions: (templateKey: string, proposalTemplateOptions: ProposalTemplateOptions) => void;
  setProposalStyleOptions: (templateKey: string, styleOptions: ProposalNS.ProposalStyleOptionsV2) => void;
  setTextAreaData: (key: string, textAreaData: ProposalTextAreaData) => void;
  setSelectedView: (templateKey: string, key: string, value: string) => void;
  cancelPDFDownload: () => void;
  toggleShortHistory: (toggled?: boolean, refreshProposalItems?: boolean) => void;
  consumeLimit: (limitType: RateLimitType) => void;
  loadProposalTemplates: () => Promise<ProposalNS.ProposalTemplate[]>;
  getKycQuestionnaires: () => Promise<KycQuestionnaire[]>;
  loadOmniRiskProfilesQuestionnaire: (formId: UUID) => Promise<OmniRiskProfilesQuestionnaire>;
  savePortfolio: (portfolio: Portfolio, contact?: Contact) => Promise<Portfolio>;
  addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
  saveComparison: (isNewlyAssigned?: boolean) => Promise<Comparison>;
  setComparisonName: (name: string) => void;
  setComparisonContact: (contact: Nullable<GenericContact>) => void;
  changeItemId: (originalId: string, newId: string) => void;
  rerunComparison: (comparison: Comparison) => Promise<Comparison>;
  changeComparisonItemsOrder: (itemIds: string[]) => void;
  changeProposalItemsOrder: (itemIds: string[]) => void;
  changeProposalContentOrder: (proposalTemplateOptions: string[]) => void;
  resetPortfolio: () => void;
  updateComparisonGroup: (id: string, name: string, products: ComparisonGroupProduct[], color?: string) => void;
  replacePortfolioInComparison: (portfolio: Portfolio, replaceId?: string, isIncomeTab?: boolean) => Promise<void>;
  setProposalLocale: (locale: Locale) => void;
};

type RouterLocationState = {
  fromComparison?: boolean;
};

type OwnProps = {
  user: User;
  proposalTemplate: ProposalNS.ProposalTemplateV2;
  onChangeTemplate: () => void;
};

type Props = RouteComponentProps<{}, {}, RouterLocationState> & StateProps & DispatchProps & OwnProps;

enum BrandingFormLocation {
  SIDEBAR = 'SIDEBAR',
  DOWNLOAD = 'DOWNLOAD',
}

type State = {
  proposalBuilderKey: string;
  proposalStatus?: ProposalStatus;
  selectedSidebarTab?: string;
  showDiscardComparisonModal: boolean;
  showCloseComparisonModal: boolean;
  showGeneratingProposalModal: boolean;
  showBrandingFormModal: boolean;
  brandingFormLocation?: BrandingFormLocation;
  redirect?: string | LocationDescriptorObject;
  downloadFormValues?: ProposalNS.ProposalDownloadFormValues;
  redirectPath?: string;
};

class ProposalPage extends Component<Props, State> {
  pageRefs: Array<React.RefObject<HTMLDivElement>>;

  constructor(props: Props) {
    super(props);
    this.pageRefs = [];
    const { comparison, omniRiskProfilesQuestionnaire, proposalTemplate, questionnaires } = this.props;

    this.props.resetProposal();

    this.initProposal();

    const selectedSidebarTab = proposalTemplate.sidebarTabs.length > 0 ? proposalTemplate.sidebarTabs[0].key : undefined;

    if (questionnaires.length === 0) {
      this.props.getKycQuestionnaires();
    }

    if (!omniRiskProfilesQuestionnaire && !!comparison?.riskProfile?.isOmniRiskProfile && !!comparison?.riskProfile?.formId) {
      this.props.loadOmniRiskProfilesQuestionnaire(comparison.riskProfile.formId);
    }

    this.state = {
      selectedSidebarTab,
      showDiscardComparisonModal: false,
      showCloseComparisonModal: false,
      showGeneratingProposalModal: false,
      showBrandingFormModal: false,
      proposalBuilderKey: new Date().getTime().toString(),
      downloadFormValues: this.getInitialBrandingInfo(),
      redirectPath: NAV_PATHS.COMPARISONS,
    };
  }

  componentDidUpdate(prevProps: Props) {
    const { proposalTemplate: prevTemplate } = prevProps;
    const { proposalTemplate: currentTemplate } = this.props;

    const prevKey = prevTemplate ? prevTemplate.key : null;
    const currentKey = currentTemplate ? currentTemplate.key : null;

    if (prevKey !== currentKey) {
      this.refreshSelectedTab();
      this.initProposal();

      this.setState({ downloadFormValues: this.getInitialBrandingInfo() });
    }
  }

  getInitialBrandingInfo = () => {
    const {
      proposalTemplate: { downloadForm },
    } = this.props;
    const storedBranding = localStorageUtils.getProposalBrandingInfo();

    if (storedBranding && downloadForm?.config.preFilledUsersInfo) {
      const { defaultValues, ...headerInfo } = storedBranding;

      return {
        headerInfo,
        defaultValues: defaultValues as MockableUserInfos,
        overrideMock: true,
      };
    }

    return undefined;
  };

  refreshSelectedTab = () => {
    const { proposalTemplate } = this.props;

    const selectedSidebarTab = proposalTemplate.sidebarTabs.length > 0 ? proposalTemplate.sidebarTabs[0].key : undefined;
    this.setState({ selectedSidebarTab });
  };

  overrideItemNames = (items: ComparisonItem[]) => {
    const { proposalTemplate } = this.props;

    // Find first item selector across all sidebar tabs - multiple item selectors is not currently supported.
    const sidebarElements = proposalTemplate.sidebarTabs.reduce((acc, tab) => acc.concat(tab.elements), [] as ProposalNS.ProposalSidebarElement[]);
    const itemSelector = sidebarElements.find(tab => tab.type === ProposalNS.ProposalItemSelectionType.ITEM_SELECTOR);

    if (itemSelector != null) {
      const { customParameters } = proposalTemplate;
      const { itemNameOverride, itemSelectors } = itemSelector as ProposalNS.ProposalItemSelection;
      let cloned = items.map(deepClone);

      // GIC filtered items (Portfolio + 100% GIC) will have a modified name
      if (itemSelectors.some(selector => itemSelectorFiltersContainsGIC(selector.filters))) {
        cloned = updateGICItemNames(cloned);
      }

      if (itemNameOverride !== undefined) {
        if (cloned.length === 1) {
          if (customParameters && customParameters.singleItemShift) {
            cloned[0].name = itemNameOverride[1];
          } else {
            cloned[0].name = itemNameOverride[0];
          }
        } else {
          for (let i: number = 0; i < itemNameOverride.length; i++) {
            cloned[i].name = itemNameOverride[i];
          }
        }
      }

      return cloned;
    }

    return items;
  };

  validatePlanningData = async (comparison: Comparison, proposalItems: ComparisonItem[]) => {
    if (!comparisonIsReadOnly(comparison) && userHasPermission(this.props.user, PERMISSIONS.PLANNING)) {
      Promise.all(
        proposalItems.map(async item => {
          const planAccountId = item.planning?.planAccountId;
          const needToFetchAccount = !item.planning?.planAccount;
          if (item.planning && planAccountId && needToFetchAccount) {
            try {
              // Use RTK Query API to fetch plan account data imperatively
              const action = planningPlanAccountsApi.endpoints.getPlanAccount.initiate({
                planId: item.planning.planId,
                accountId: planAccountId,
              });

              const result = await (store as any).dispatch(action);

              if ('data' in result && result.data) {
                item.planning.planAccount = result.data;
              }
            } catch (error) {
              console.warn('Failed to fetch plan account:', error);
            }
          }
        }),
      )
        .then(() => {
          this.props.setProposalItems(proposalItems);
        })
        .catch(() => {
          notification.error(formatMessage('Proposal.Planning.Notifications.Failed'));
          // Ensure the proposal is still able to render if the planning API fails
          this.props.setProposalItems(proposalItems);
        });
    }
  };

  initProposalBmoGIFItems = (comparison: Comparison): void => {
    const { proposalTemplate } = this.props;
    const maxItemsSelected: number = getMaxNumberOfItems(proposalTemplate, proposalTemplate.options.styleOptions.orientation);

    const proposalItems: BMOGIFItem[] = comparison.gifItems !== undefined ? comparison.gifItems : [];
    this.props.setProposalGifItems(proposalItems.slice(0, maxItemsSelected));
  };

  initProposalComparisonItems = (comparison: Comparison): void => {
    const { proposalTemplate } = this.props;

    const { allowedItemTypes } = proposalTemplate.itemManagement;

    const validItems = comparison.items.filter(item => allowedItemTypes.indexOf(item.type) !== -1);

    proposalTemplate.options.styleOptions.orientation = getDefaultOrientation(proposalTemplate, validItems.length);
    const maxItemsSelected: number = getMaxNumberOfItems(proposalTemplate, proposalTemplate.options.styleOptions.orientation);

    // Find first item selector across all sidebar tabs - multiple item selectors is not currently supported.
    const sidebarElements = proposalTemplate.sidebarTabs.reduce((acc, tab) => acc.concat(tab.elements), [] as ProposalNS.ProposalSidebarElement[]);
    const itemSelector = sidebarElements.find(
      tab => tab.type === ProposalNS.ProposalItemSelectionType.ITEM_SELECTOR || tab.type === ProposalNS.ProposalItemSelectionType.ITEM_LISTER,
    );

    let proposalItems: ComparisonItem[] = [];
    if (itemSelector != null) {
      const { customParameters } = proposalTemplate;
      const { itemNameOverride, itemSelectors } = itemSelector as ProposalNS.ProposalItemSelection;
      let firstValidItems: ComparisonItem[] = [];

      if (itemSelectors && itemSelectors.some(selector => selector.filters !== undefined)) {
        for (const option of itemSelectors) {
          if (option.filters) {
            let filteredItems = filterItemsByItemSelectionFilters(validItems, option.filters);

            if (itemSelectorFiltersContainsGIC(option.filters)) {
              filteredItems = updateGICItemNames(filteredItems);
            }

            if (filteredItems.length !== 0) {
              firstValidItems.push(filteredItems[0]);
            }
          }
        }
      } else {
        firstValidItems = validItems.slice(0, maxItemsSelected).map(item => deepClone(item));
      }

      if (itemNameOverride !== undefined) {
        if (firstValidItems.length === 1) {
          if (customParameters && customParameters.singleItemShift) {
            firstValidItems[0].name = itemNameOverride[1];
          } else {
            firstValidItems[0].name = itemNameOverride[0];
          }
        } else {
          for (let i: number = 0; i < itemNameOverride.length; i++) {
            firstValidItems[i].name = itemNameOverride[i];
          }
        }
      }

      proposalItems = firstValidItems;
    } else {
      const itemLister = proposalTemplate.sidebarTabs[0].elements.find(tab => tab.type === ProposalNS.ProposalItemSelectionType.ITEM_LISTER);
      if (itemLister != null) {
        proposalItems = validItems.slice(0, maxItemsSelected);
      } else {
        proposalItems = comparison.items;
      }
    }
    this.validatePlanningData(comparison, proposalItems);
    this.props.setProposalItems(proposalItems);
  };

  initProposalItems = (comparison: Comparison): void => {
    const { proposalTemplate } = this.props;
    const { allowedItemTypes } = proposalTemplate.itemManagement;

    if (allowedItemTypes.indexOf(ProposalNS.ProposalItemType.BmoGIF) !== -1) {
      this.initProposalBmoGIFItems(comparison);
    } else {
      this.initProposalComparisonItems(comparison);
    }
  };

  initProposal = (): void => {
    const { comparison, comparisonCorrelations, proposalTemplate, user } = this.props;

    if (!comparison) {
      return;
    }

    // TODO - add GIF items somehow

    const proposalDate = comparison.comparisonType !== COMPARISON_TYPE.DRAFT && comparison.lastRunAt != null ? comparison.lastRunAt : new Date();
    this.props.setProposalDate(proposalDate);

    if (comparison.contact && comparison.contact.fullName) {
      const { contact } = comparison;
      this.props.setProposalContact(contact);
    }

    this.initProposalItems(comparison);
    this.props.setProposalItemsCorrelation(comparisonCorrelations);

    // Template might not define this value
    if (!proposalTemplate.options.styleOptions.paperFormat) {
      proposalTemplate.options.styleOptions.paperFormat = PROPOSAL_PAPER_FORMAT.LETTER;
    }

    proposalTemplate.options.hiddenLayouts = [];

    const cachedOptions: ProposalTemplateOptions | null = localStorageUtils.getProposalV2Options(proposalTemplate.key);
    if (cachedOptions !== null) {
      // local - we currently assume that all templates support both locales.
      proposalTemplate.options.locale = cachedOptions.locale;

      // theme
      if (proposalTemplate.themes.find(theme => theme.key === cachedOptions.theme)) {
        proposalTemplate.options.theme = cachedOptions.theme;
      }

      // sections - if the template support section management, we currently simply used the last selections.
      // Maybe we need for this to be template specific in the future.
      const hasProposalSections = proposalTemplate.sidebarTabs.some(sidebarTab =>
        sidebarTab.elements.some(element => element.type === ProposalSidebarOptions.PROPOSAL_SECTIONS),
      );

      if (hasProposalSections) {
        const hiddenLayouts = cachedOptions.hiddenLayouts !== undefined ? cachedOptions.hiddenLayouts : [];
        proposalTemplate.options.hiddenLayouts = hiddenLayouts;

        const sectionKeys = getProposalTemplateSectionKeys(proposalTemplate);
        let sectionCommonKeys = proposalTemplate.options.sections;
        if (cachedOptions.sections != null) {
          sectionCommonKeys = cachedOptions.sections.filter(x => sectionKeys.includes(x));
          proposalTemplate.options.sections = uniqueArray([...sectionCommonKeys]);
        }

        const divisionKeys = getProposalTemplateDivisionKeys(proposalTemplate);
        if (cachedOptions.divisions === undefined) {
          cachedOptions.divisions = proposalTemplate.options.divisions !== undefined ? proposalTemplate.options.divisions : divisionKeys;
        }

        const divisionCommonKeys = divisionKeys.filter(x => cachedOptions.divisions.includes(x));
        proposalTemplate.options.divisions = uniqueArray([...divisionCommonKeys]);

        proposalTemplate.options.views = { ...proposalTemplate.options.views, ...cachedOptions.views };

        if (cachedOptions.views != null) {
          for (const key of Object.keys(cachedOptions.views)) {
            if (sectionCommonKeys.indexOf(key) !== -1) {
              proposalTemplate.options.views[key] = cachedOptions.views[key];
            }
          }
        }
      }

      // banners
      if (cachedOptions.styleOptions?.banners != null) {
        proposalTemplate.options.styleOptions.banners = cachedOptions.styleOptions.banners;
      }

      // orientation - at the moment, we do not override the orientation and always go for the default.

      // paper format
      if (cachedOptions.styleOptions?.paperFormat != null) {
        proposalTemplate.options.styleOptions.paperFormat = cachedOptions.styleOptions.paperFormat;
      }

      // includeFundProfiles
      if (cachedOptions.styleOptions?.includeFundProfiles != null) {
        proposalTemplate.options.styleOptions.includeFundProfiles = cachedOptions.styleOptions.includeFundProfiles;
      }

      // includePortfolioProfiles
      if (cachedOptions.styleOptions?.includePortfolioProfiles != null) {
        proposalTemplate.options.styleOptions.includePortfolioProfiles = cachedOptions.styleOptions.includePortfolioProfiles;
      }

      // comments
      proposalTemplate.options.comments = cachedOptions.comments;
    }

    if (!user.appConfig.appLocales.includes(proposalTemplate.options.locale)) {
      proposalTemplate.options.locale = user.appConfig.appLocales[0] || LOCALE.EN_CA;
    }

    this.props.setProposalTemplateOptions(proposalTemplate.key, proposalTemplate.options);

    if (comparison.gifItems) {
      this.props.setProposalGifItems(comparison.gifItems);
    }

    if (comparison.riskProfile) {
      this.props.setProposalRiskProfile(comparison.riskProfile);
    }
  };

  handleProposalClose = () => {
    const { comparison, user } = this.props;

    this.setState({ redirectPath: NAV_PATHS.COMPARISONS }, () => {
      if (comparison && comparison.isDirty) {
        return user.appConfig.mode === AppMode.LINK ? this.showCloseComparisonModal() : this.showDiscardComparisonModal();
      }

      this.onCloseComparison();
    });
  };

  goBackToComparison = () => {
    const { comparison, tab, originPath } = this.props;
    if (!comparison || !comparison._id) {
      notification.errorFromKey('COMPARISON_NOT_FOUND');
      return this.setState({
        redirect: NAV_PATHS.COMPARISONS,
      });
    }

    const comparisonPathRoot = !comparison._id.startsWith('new-') ? NAV_PATHS.COMPARISON_DETAILS : NAV_PATHS.COMPARISON;

    const redirect = {
      pathname: `${generatePath(comparisonPathRoot, {
        comparisonId: comparison._id || '',
      })}${tab ?? COMPARISON_NAV_PATHS.PERFORMANCE}`,
      search: comparison.contact?._id && !comparison._id.startsWith('new-') ? `?contact=${comparison.contact?._id}` : undefined,
    };

    if (originPath) {
      Object.assign(redirect, { state: { from: originPath } });
    }

    return this.setState({ redirect });
  };

  onSaveComparison = (isNewlyAssigned?: boolean) => this.props.saveComparison(isNewlyAssigned);

  closeDiscardComparisonModal = () => {
    this.setState({ showDiscardComparisonModal: false });
  };

  showDiscardComparisonModal = () => {
    this.setState({ showDiscardComparisonModal: true });
  };

  closeCloseComparisonModal = () => {
    this.setState({ showCloseComparisonModal: false });
  };

  showCloseComparisonModal = () => {
    this.setState({ showCloseComparisonModal: true });
  };

  onCloseComparison = () => {
    const { comparison, originPath } = this.props;
    const { redirectPath } = this.state;
    if (!comparison) return;

    // Clear branding info for this proposal from localStorage
    localStorageUtils.clearProposalBrandingInfo();

    this.props.closeComparison(comparison);

    if (originPath) {
      return this.props.history.push(originPath.pathname);
    }

    this.props.history.push(redirectPath ?? NAV_PATHS.COMPARISONS);
  };

  onLocaleChange = (locale: Locale) => {
    const { proposalTemplate } = this.props;
    this.props.setProposalLocale(locale);
    this.props.setProposalTemplateLocale(proposalTemplate.key, locale);
  };

  onTabChange = (tab: NavigationItem) => {
    if (!tab.id) {
      return;
    }

    const selectedTab = this.props.proposalTemplate.sidebarTabs.find(element => element.key === tab.id);

    this.setState({ selectedSidebarTab: selectedTab ? selectedTab.key : undefined });
  };

  onToggleProposalHiddenLayout = (toggledHiddenLayout: string, selected: boolean) => {
    const {
      proposalTemplateOptions: { hiddenLayouts: prevHiddenLayouts },
      proposalTemplate,
      setProposalTemplateHiddenLayouts,
    } = this.props;

    if (!proposalTemplate) {
      return;
    }

    let newHiddenLayouts = [];
    if (selected) {
      newHiddenLayouts = prevHiddenLayouts.concat(toggledHiddenLayout);
    } else {
      newHiddenLayouts = prevHiddenLayouts.filter(hiddenLayout => hiddenLayout !== toggledHiddenLayout);
    }

    setProposalTemplateHiddenLayouts(proposalTemplate.key, newHiddenLayouts);
  };

  onToggleProposalSection = (toggledSection: string, selected: boolean) => {
    const {
      proposalTemplateOptions: { sections: prevSections },
    } = this.props;

    if (!this.props.proposalTemplate) {
      return;
    }

    const proposalTemplate = this.props.proposalTemplate as ProposalNS.ProposalTemplateV2;

    let newSections = [];
    if (selected) {
      newSections = prevSections.concat(toggledSection);
    } else {
      newSections = prevSections.filter(section => section !== toggledSection);
    }

    if (proposalTemplate.linkedSections !== undefined) {
      for (const linkDefinition of proposalTemplate.linkedSections) {
        if (linkDefinition.sections.indexOf(toggledSection) !== -1) {
          const otherSectionKeys = linkDefinition.sections.filter(section => section !== toggledSection);

          switch (linkDefinition.type) {
            case ProposalNS.ProposalSectionLinkType.OPPOSITE:
              if (selected) {
                newSections = newSections.filter(section => otherSectionKeys.indexOf(section) === -1);
              }
              break;

            case ProposalNS.ProposalSectionLinkType.OPPOSITE_MANDATORY:
              if (selected) {
                newSections = newSections.filter(section => otherSectionKeys.indexOf(section) === -1);
              } else {
                newSections = newSections.concat(otherSectionKeys);
              }
              break;
            case ProposalNS.ProposalSectionLinkType.SAME:
              if (selected) {
                if (newSections[newSections.length - 1]) {
                  newSections.pop();
                  newSections = newSections.concat(linkDefinition.sections);
                } else {
                  newSections = newSections.concat(otherSectionKeys);
                }
              } else {
                newSections = newSections.filter(section => otherSectionKeys.indexOf(section) === -1);
              }
              break;
          }
        }
      }
    }
    this.props.setProposalTemplateSections(proposalTemplate.key, newSections);
  };

  onToggleProposalDivision = (toggledDivision: string, selected: boolean) => {
    const {
      proposalTemplateOptions: { divisions: prevDivisions },
      proposalTemplate,
    } = this.props;

    if (!this.props.proposalTemplate) {
      return;
    }

    let newDivisions = [];
    if (selected) {
      newDivisions = prevDivisions.concat(toggledDivision);
    } else {
      newDivisions = prevDivisions.filter(section => section !== toggledDivision);
    }

    this.props.setProposalTemplateDivisions(proposalTemplate.key, newDivisions);
  };

  onItemsSelection = (items: ComparisonItem[]) => {
    const { proposalTemplate } = this.props;

    // Temporary hack specific to MD in order to fix item name in disclaimers not updating
    // Will need proper testing and some refactoring of initProposal before merging to other clients
    let clonedItems = null;
    if (proposalTemplate.key === 'MD_PIC' || proposalTemplate.key === 'MD_SIGNATURE' || proposalTemplate.key === 'BMO_GIF_MARKETING') {
      clonedItems = this.overrideItemNames(items);
    }

    this.props.setProposalItems(clonedItems || items);
  };

  onItemPurposeFlagUpdate = (itemId: string, flags: string[]) => {
    this.props.setProposalItemFlags(itemId, flags);
    this.props.setComparisonItemFlags(itemId, flags);
  };

  onGifItemsSelection = (items: BMOGIFItem[]) => {
    this.props.setProposalGifItems(items);
  };

  onToggleGifBaseScenario = (value: boolean) => {
    this.props.toggleGifHideBaseScenario(value);
  };

  onProposalStyleOptionsChange = (styleOptions: ProposalNS.ProposalStyleOptionsV2) => {
    const { proposalTemplate } = this.props;
    this.props.setProposalStyleOptions(proposalTemplate.key, styleOptions);
  };

  getNewProposalFilename = () => {
    const { comparison, proposalItems, proposalTemplateOptions, proposalTemplate, proposalContact, user } = this.props;
    const date = moment().format('YYYY-MM-DD-HHmmss');

    if (!comparison) {
      return '';
    }

    const hasContactLocale = userHasPermission(user, PERMISSIONS.CONTACTS_LOCALE);
    const locale: Locale = hasContactLocale && proposalContact?.locale != null ? proposalContact.locale : proposalTemplateOptions.locale;
    if (userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_PDF_METADATA) && proposalTemplate?.proposalPdfMetadata?.title?.[locale]) {
      return `${proposalTemplate.proposalPdfMetadata.title[locale]}-${date}.pdf`;
    }

    if (comparison.householdName) {
      return `${comparison.householdName}-${date}.pdf`;
    }

    if (comparison.contact) {
      return `${getComparisonContactName(comparison)}-${date}.pdf`;
    }

    const itemTypes = uniqueArray(proposalItems.map(item => item.type));

    let name;
    if (itemTypes.length > 1) {
      name = formatMessage('Proposal.Types.MIXED');
    } else {
      name = formatMessage(`Proposal.Types.${itemTypes[0].toUpperCase()}`);
    }

    return `${formatMessage('Proposal.FilePrefix', { name })}-${date}.pdf`;
  };

  downloadPDF = () => {
    const { proposalTemplate, user, context } = this.props;
    const { downloadFormValues } = this.state;

    if (proposalTemplate.downloadForm && proposalTemplate.downloadForm.config.showBeforeDownload !== false) {
      this.showDownloadForm(BrandingFormLocation.DOWNLOAD);
      return;
    }

    const branding: Dictionary<string | undefined> = {};
    if (downloadFormValues) {
      branding.firstName = downloadFormValues.headerInfo.fname;
      branding.lastName = downloadFormValues.headerInfo.lname;
      branding.email = downloadFormValues.headerInfo.email;
    } else {
      branding.firstName = context?.advisor?.firstName || this.props.user.fname;
      branding.lastName = context?.advisor?.lastName || this.props.user.lname;
      branding.email = user.email;
    }

    this.processDownloadPDF(branding);
  };

  onSubmitBrandingInfo = (values: ProposalNS.ProposalDownloadFormValues) => {
    const {
      proposalTemplate: { downloadForm },
    } = this.props;

    if (downloadForm?.config.preFilledUsersInfo) {
      localStorageUtils.setProposalBrandingInfo({
        fname: values.headerInfo.fname,
        lname: values.headerInfo.lname,
        email: values.headerInfo.email,
        company: values.headerInfo.company,
        title: values.headerInfo.title,
        phone: values.headerInfo.phone,
        date: values.headerInfo.date,
        clientName: values.headerInfo.clientName,
        defaultValues: values.defaultValues,
      });
    }

    this.setState({ downloadFormValues: values }, () => {
      if (values.headerInfo.date) {
        const convertedDate = convertStringToDate(values.headerInfo.date);
        if (convertedDate) {
          this.props.setProposalDate(convertedDate);
        }
      }

      // If the form came from clicking on Download, proceed with the download
      if (this.state.brandingFormLocation === BrandingFormLocation.DOWNLOAD) {
        this.processDownloadPDF({
          firstName: values.headerInfo.fname,
          lastName: values.headerInfo.lname,
          email: values.headerInfo.email,
        });
      }

      this.hideDownloadForm();
    });

    return Promise.resolve();
  };

  processDownloadPDF = (brandingInfo: Nullable<Dictionary<string | undefined>>) => {
    this.showGeneratingProposalModal();

    const title = this.getNewProposalFilename();
    const { user, proposalTemplate } = this.props;
    const proposalPdfMetadata = userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_PDF_METADATA) ? proposalTemplate?.proposalPdfMetadata : undefined;

    this.props
      .downloadProposalPDF(title, brandingInfo, proposalPdfMetadata)
      .then(response => {
        const { data: blob, status } = response;
        const shouldDownload = blob && blob.size > 0;
        const shouldDisplaySentConfirmation = status === HttpStatusCode.NO_CONTENT;

        heapUtils.track(HEAP_CUSTOM_EVENTS.DOWNLOAD_PROPOSAL, {
          hasEnterpriseModel: this.props.proposalItems.some(proposalItem => proposalItem.enterprise),
          template: this.props.proposalTemplate.key,
        });
        this.props.consumeLimit(RateLimitType.PROPOSALS);

        if (shouldDownload) {
          downloadFile(blob, title);
          this.setState({ showGeneratingProposalModal: false });
        } else if (shouldDisplaySentConfirmation) {
          this.setState({ proposalStatus: ProposalStatus.SUCCESS });
        } else {
          throw new Error('Unexpected response');
        }
      })
      .catch(err => {
        if (err && err.status === 502 && err.data instanceof Blob) {
          downloadFile(err.data, title);
          this.setState({ proposalStatus: ProposalStatus.TRANSFER_ERROR });
        } else {
          this.setState({ proposalStatus: ProposalStatus.ERROR });
        }
      });
  };

  showGeneratingProposalModal = () => {
    this.setState({ showGeneratingProposalModal: true, proposalStatus: ProposalStatus.LOADING });
  };

  closeGeneratingProposalModal = () => {
    this.props.cancelPDFDownload();
    this.setState({ showGeneratingProposalModal: false });
  };

  onToggleShortHistory = (toggled?: boolean) => this.props.toggleShortHistory(toggled, true);

  showDownloadForm = (location: BrandingFormLocation) => {
    this.setState({ showBrandingFormModal: true, brandingFormLocation: location });
  };

  hideDownloadForm = () => {
    this.setState({ showBrandingFormModal: false, brandingFormLocation: undefined });
  };

  onSidebarEditBrandingInfo = () => {
    this.showDownloadForm(BrandingFormLocation.SIDEBAR);
  };

  onChangeItemOrder = (itemId: string, referenceId: string, position?: DropItemPosition) => {
    const { proposalItems, comparison } = this.props;

    if (!comparison) {
      return;
    }

    const itemIds = comparison.items.reduce((ids: string[], currentItem) => {
      if (currentItem._id === referenceId && position === DropItemPosition.Before) {
        ids.push(itemId);
      }

      if (currentItem._id !== itemId) {
        ids.push(currentItem._id);
      }

      if (currentItem._id === referenceId && position === DropItemPosition.After) {
        ids.push(itemId);
      }

      return ids;
    }, []);

    this.props.changeComparisonItemsOrder(itemIds);

    // Filter item ids to keep only items that are used in the proposal
    const proposalItemIds = itemIds.filter(id => proposalItems.some(proposalItem => proposalItem._id === id));
    this.props.changeProposalItemsOrder(proposalItemIds);
  };

  updateListOrder = (list: string[] | any, index: number, extraItems: string[] | any) => list.splice(...[index, 0], ...extraItems);

  getOrderedSections = (
    sections: Array<string>,
    itemId: string,
    referenceId: string,
    position?: DropItemPosition,
    items?: Array<string>,
    references?: Array<string>,
  ) => {
    const beforeReference = references?.length ? references[0] : referenceId;
    const afterReference = references?.length ? references[references.length - 1] : referenceId;

    return sections.reduce((list: string[], currentItem) => {
      if (currentItem === beforeReference && position === DropItemPosition.Before) {
        items?.length ? list.push(...items) : list.push(itemId);
      }

      if (currentItem !== itemId) {
        list.push(currentItem);
      }

      if (currentItem === afterReference && position === DropItemPosition.After) {
        items?.length ? list.push(...items) : list.push(itemId);
      }

      return list;
    }, []);
  };

  onChangeContentOrder = (itemId: string, referenceId: string, position?: DropItemPosition) => {
    const {
      proposalTemplateOptions: { sections },
      proposalTemplate,
    } = this.props;

    if (!sections) {
      return;
    }

    let orderedSections = sections;

    if (!proposalTemplate.linkedSections || !proposalTemplate.linkedSections.length) {
      orderedSections = this.getOrderedSections(sections, itemId, referenceId, position);
    }

    if (proposalTemplate.linkedSections !== undefined && proposalTemplate.linkedSections.length) {
      const linkedSameSections = proposalTemplate.linkedSections.filter(section => section.type === ProposalNS.ProposalSectionLinkType.SAME);
      const linkedGroupedSections = linkedSameSections.map(section => section.sections);
      let isOrdered = false;

      for (const linkDefinition of proposalTemplate.linkedSections) {
        if (linkDefinition.type === ProposalNS.ProposalSectionLinkType.SAME) {
          const linkedSections = linkDefinition.sections;

          if (linkedGroupedSections.flat().indexOf(referenceId) !== -1 && linkedSections.indexOf(itemId) !== -1) {
            const linkedReferences = linkedGroupedSections.filter(section => section.indexOf(referenceId) !== -1).flat();
            const unlinkedSections = sections.filter(section => !linkedSections.includes(section));

            orderedSections = this.getOrderedSections(unlinkedSections, itemId, referenceId, position, linkedSections, linkedReferences);
            isOrdered = true;
          }

          if (!isOrdered && linkedSections.indexOf(itemId) !== -1) {
            const unlinkedSections = sections.filter(section => !linkedSections.includes(section));

            orderedSections = this.getOrderedSections(unlinkedSections, itemId, referenceId, position, linkedSections);
            isOrdered = true;
          }

          if (!isOrdered && linkedSections.indexOf(itemId) === -1 && linkedSections.indexOf(referenceId) !== -1) {
            orderedSections = this.getOrderedSections(sections, itemId, referenceId, position, [], linkedSections);
            isOrdered = true;
          }
        }
      }

      if (!isOrdered) {
        orderedSections = this.getOrderedSections(sections, itemId, referenceId, position);
        isOrdered = true;
      }
    }

    this.props.changeProposalContentOrder(orderedSections);
    this.props.setProposalTemplateSections(proposalTemplate.key, orderedSections);
  };

  getDownloadForm = () => {
    const { downloadFormValues } = this.state;
    const { context, user } = this.props;

    const { downloadForm } = this.props.proposalTemplate;

    if (!downloadForm) {
      return null;
    }

    switch (downloadForm.key) {
      case ProposalNS.ProposalDownloadFormKeys.AdvisorBranding: {
        let initialValues;

        if (downloadForm.config.preFilledUsersInfo) {
          if (downloadFormValues) {
            // Set the initialValues sent to the form as the values currently present in
            // the localStorage
            initialValues = {
              ...downloadFormValues.headerInfo,
              date: convertDateToString(this.props.proposalDate ?? null) || undefined,
              defaultValues: downloadFormValues.defaultValues,
            };
          } else {
            // Define original user data to be used as the basis for the revert functionality
            const defaultValues = {
              fname: context?.advisor?.firstName || this.props.user.fname,
              lname: context?.advisor?.lastName || this.props.user.lname,
              company: context?.advisor?.dealerName?.short?.[this.props.locale] || this.props.user.company,
              title: this.props.user.title,
              phone: this.props.user.phone,
              email: this.props.user.email,
              date: convertDateToString(this.props.proposalDate ?? null) || undefined,
              clientName: this.props.comparison?.contact?.fullName || '',
            };

            // Set the initialValues sent to the form as the original user
            // and override the 'fname' and 'lname' value if need be
            initialValues = {
              ...defaultValues,
              defaultValues,
            };
          }
        }

        const showSectionHeader = downloadForm.config.showSectionHeader != null ? downloadForm.config.showSectionHeader : true;
        return (
          <AdvisorBrandingForm
            initialValues={initialValues}
            onSubmit={this.onSubmitBrandingInfo}
            fields={downloadForm.config.fields}
            showSectionHeader={showSectionHeader}
            allowFormReset={!!downloadForm.config.preFilledUsersInfo}
          />
        );
      }
      default:
        return null;
    }
  };

  getProposalContact = (): ProposalNS.ProposalContact | undefined => {
    const {
      proposalTemplate,
      proposalTemplateOptions: { locale },
    } = this.props;
    const { downloadFormValues } = this.state;

    const hasBrandingForm = proposalTemplate.downloadForm?.key === ProposalNS.ProposalDownloadFormKeys.AdvisorBranding;

    // If the proposal is meant to be branded through a form
    if (hasBrandingForm && !proposalTemplate.downloadForm?.config.preFilledUsersInfo) {
      // Show the sample data unless the `overrideMock` flag was set to true
      if (!downloadFormValues || !downloadFormValues.overrideMock) {
        return {
          fullName: formatMessageWithLocale('Proposal.PlaceholderInfo.contactFullName', locale),
        };
      }
    }

    if (downloadFormValues && downloadFormValues.contact) {
      return {
        fullName: downloadFormValues.contact,
      };
    }

    return this.props.proposalContact;
  };

  getKYCQuestionnaire = (): KycQuestionnaire | undefined => {
    const { riskProfile, questionnaires } = this.props;

    return questionnaires.find(questionnaire => questionnaire._id === riskProfile?.questionnaireId);
  };

  getInvestorQuestionnaire = (): KycQuestionnaire | OmniRiskProfilesQuestionnaire | undefined => {
    const { comparison, omniRiskProfilesQuestionnaire, questionnaires, riskProfile, user } = this.props;
    if (userHasPermissionEnabled(user, PERMISSIONS.OMNI_RISK_PROFILE) && omniRiskProfilesQuestionnaire && !!comparison?.riskProfile?.isOmniRiskProfile) {
      return omniRiskProfilesQuestionnaire;
    }
    return questionnaires.find(questionnaire => questionnaire._id === riskProfile?.questionnaireId);
  };

  getProposalUser = (): MockableUserInfos => {
    const {
      proposalTemplate,
      proposalTemplateOptions: { locale },
    } = this.props;
    const { downloadFormValues } = this.state;

    const brandingForm = proposalTemplate.downloadForm?.key === ProposalNS.ProposalDownloadFormKeys.AdvisorBranding ? proposalTemplate.downloadForm : null;

    // If the proposal is meant to be branded through a form
    if (brandingForm && !brandingForm.config.preFilledUsersInfo && (!downloadFormValues || !downloadFormValues.overrideMock)) {
      const fields = ['fname', 'lname', 'email', 'company', 'title', 'phone'];

      // Loop through the user form fields and generate the localized info
      const user = fields.reduce(
        (acc, field) => {
          acc[field] = formatMessageWithLocale(`Proposal.PlaceholderInfo.${field}`, locale);
          return acc;
        },
        {} as Dictionary<string | undefined>,
      );

      return {
        ...user,
        isMock: true,
      };
    }

    if (downloadFormValues && downloadFormValues.overrideMock) {
      return {
        ...this.props.user,
        ...downloadFormValues.headerInfo,
      };
    }

    return {
      ...this.props.user,
      fname: this.props.context?.advisor?.firstName || this.props.user.fname,
      lname: this.props.context?.advisor?.lastName || this.props.user.lname,
      company: this.props.context?.advisor?.dealerName?.short?.[this.props.locale] || this.props.user.company,
    };
  };

  refreshTemplate = async () => {
    const { proposalTemplate } = this.props;
    const templates = await this.props.loadProposalTemplates();

    const matchedTemplate = templates.find(t => t.key === proposalTemplate.key);
    if (matchedTemplate) {
      this.props.setProposalTemplate(matchedTemplate);
    }

    this.setState({ proposalBuilderKey: new Date().getTime().toString() });
  };

  onSavePortfolio = (portfolio: Portfolio, contact?: GenericContact, reset = true): Promise<Portfolio> => {
    const rawComparison = this.props.comparison;
    if (rawComparison == null) {
      return Promise.reject();
    }

    // Find id of the item pre-save - will be used to replace the proper item.
    const comparisonItem = rawComparison.items.find(item => item._id === portfolio._id);
    const id = comparisonItem != null && comparisonItem.portfolioType === PORTFOLIO_TYPES.UNASSIGNED && contact != null ? undefined : portfolio._id;

    return this.props.savePortfolio({ ...portfolio, _id: id }, contact).then(async (newPortfolio: Portfolio) => {
      if (reset) {
        this.props.resetPortfolio();
      }

      const groups = rawComparison.items.filter(
        item => item.type === COMPARISON_ITEM_TYPES.GROUP && item.groupItems?.some(groupItem => groupItem.itemId === portfolio._id),
      );

      await Promise.all(
        groups.map(group => this.props.updateComparisonGroup(group._id, formatLocalizedString(group.name), group.groupItems || [], group.color)),
      );

      if (comparisonItem !== undefined) {
        return this.props.replacePortfolioInComparison(newPortfolio, comparisonItem._id, false).then(() => newPortfolio);
      }

      return this.props.addPortfoliosToComparison([newPortfolio]).then(() => newPortfolio);
    });
  };

  redirectToContactPage = () => {
    const { comparison } = this.props;
    if (!comparison || !comparison.contact || !comparison.contact._id) return;

    this.setState({ redirectPath: NAV_PATHS.CONTACT_DETAILS.replace(':contactId', comparison.contact._id) }, () => {
      if (comparison.isDirty) {
        return this.showDiscardComparisonModal();
      }
      this.onCloseComparison();
    });
  };

  render() {
    const {
      comparison,
      proposalItems,
      proposalItemsCorrelation,
      proposalGifItems,
      proposalTemplate,
      proposalTemplateOptions,
      riskProfile,
      toolbarOptions,
      user,
      proposalDate,
      templates,
      gifHideBaseScenario,
      locale,
      context,
      rationaleAnswers,
    } = this.props;

    const {
      proposalStatus,
      redirect,
      selectedSidebarTab,
      showDiscardComparisonModal,
      showGeneratingProposalModal,
      showBrandingFormModal,
      proposalBuilderKey,
      showCloseComparisonModal,
    } = this.state;

    let comparisonToolbarOptions: ComparisonToolbarOptions = {};
    if (comparison) {
      comparisonToolbarOptions = {
        ...comparison.options,
        riskPeriod: comparison.riskPeriod,
        calendarYearPeriod: comparison.calendarYearPeriod,
        fundRiskPeriod: comparison.fundRiskPeriod,
        portfolioRiskPeriod: comparison.portfolioRiskPeriod,
        groupRiskPeriod: comparison.groupRiskPeriod,
        fundCalendarYearPeriod: comparison.fundCalendarYearPeriod,
        portfolioCalendarYearPeriod: comparison.portfolioCalendarYearPeriod,
        groupCalendarYearPeriod: comparison.groupCalendarYearPeriod,
      };
    }

    if (!comparison) {
      return <Redirect to={NAV_PATHS.COMPARISON} />;
    }

    if (redirect) {
      return <Redirect to={redirect} />;
    }

    if (!proposalTemplate) {
      return null;
    }

    const contact = this.getProposalContact();
    const proposalUser = this.getProposalUser();
    const investorQuestionnaire = this.getInvestorQuestionnaire();

    return (
      <>
        <div className="proposal-v2">
          <div className="proposal-container">
            <DelegateBanner />
            <AnnouncementBanner />
            <div className="proposal-container-navigation">
              {user.appConfig.mode === AppMode.LINK ? (
                <LinkTopHeader
                  user={user}
                  handleProposalClose={this.handleProposalClose}
                  refreshTemplate={this.refreshTemplate}
                  goBackToComparison={this.goBackToComparison}
                />
              ) : (
                <>
                  <div className={classNames({ 'desktop-navigation': userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_ACCESSIBILITY) })}>
                    <DefaultHeader
                      user={user}
                      comparison={comparison}
                      isSaving={this.props.isSaving}
                      theme={this.props.theme}
                      isEditVisible={this.props.isEditVisible}
                      isEditPinned={this.props.isEditPinned}
                      canPortfolioSave={this.props.canPortfolioSave}
                      onSaveComparison={this.onSaveComparison}
                      onSavePortfolio={this.onSavePortfolio}
                      setComparisonName={this.props.setComparisonName}
                      redirectToContactPage={this.redirectToContactPage}
                      changeItemId={this.props.changeItemId}
                      refreshTemplate={this.refreshTemplate}
                      handleProposalClose={this.handleProposalClose}
                      goBackToComparison={this.goBackToComparison}
                      setComparisonContact={this.props.setComparisonContact}
                    />
                  </div>
                  {userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_ACCESSIBILITY) && (
                    <div className="mobile-navigation">
                      <MobileHeader
                        comparison={comparison}
                        isSaving={this.props.isSaving}
                        goBackToComparison={this.goBackToComparison}
                        onChangeTemplate={this.props.onChangeTemplate}
                        onSaveComparison={this.onSaveComparison}
                        redirectToContactPage={this.redirectToContactPage}
                        refreshTemplate={this.refreshTemplate}
                        setComparisonName={this.props.setComparisonName}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            {!userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_TOOLBOX_NEW_MENU) && (
              <div className="proposal-container-header">
                <div className="proposal-header-subnav">
                  <div className="proposal-header-subnav-options">
                    <PrimaryNavigation
                      replaceUrl
                      className="proposal-tabs"
                      items={getSidebarNavigationTabs(user, proposalTemplate)}
                      selectedId={selectedSidebarTab}
                      onChange={this.onTabChange}
                    />

                    <div className="proposal-actions">
                      <DownloadPDFButton user={user} downloadPdf={this.downloadPDF} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {proposalItems !== undefined && (
              <ProposalBuilderComponent
                key={proposalBuilderKey}
                user={user}
                locale={locale}
                advisorContext={context}
                comparison={comparison}
                template={proposalTemplate as ProposalNS.ProposalTemplateV2}
                proposalItems={proposalItems}
                proposalItemsCorrelation={proposalItemsCorrelation}
                proposalGifItems={proposalGifItems}
                proposalContact={contact}
                proposalDate={proposalDate}
                proposalUser={proposalUser}
                proposalTemplateOptions={proposalTemplateOptions}
                rationaleAnswers={rationaleAnswers}
                onLocaleChange={this.onLocaleChange}
                selectedSidebarTab={selectedSidebarTab}
                onToggleHiddenLayout={this.onToggleProposalHiddenLayout}
                onToggleSection={this.onToggleProposalSection}
                onToggleDivision={this.onToggleProposalDivision}
                onItemsSelection={this.onItemsSelection}
                onItemPurposeFlagUpdate={this.onItemPurposeFlagUpdate}
                onGifItemsSelection={this.onGifItemsSelection}
                onToggleGifBaseScenario={this.onToggleGifBaseScenario}
                riskProfile={riskProfile}
                investorQuestionnaire={investorQuestionnaire}
                comparisonToolbarOptions={comparisonToolbarOptions}
                setToolbarOption={this.props.setToolbarOption}
                calculateHistoricalScenarios={this.props.calculateHistoricalScenarios}
                toolbarOptions={toolbarOptions}
                onChangeTemplate={this.props.onChangeTemplate}
                onProposalStyleOptionsChange={this.onProposalStyleOptionsChange}
                setTextAreaData={this.props.setTextAreaData}
                setSelectedView={this.props.setSelectedView}
                templates={templates}
                gifHideBaseScenario={gifHideBaseScenario}
                onToggleShortHistory={this.onToggleShortHistory}
                shortHistory={comparison.shortHistory}
                onEditBrandingInfo={this.onSidebarEditBrandingInfo}
                onChangeItemOrder={this.onChangeItemOrder}
                onChangeContentOrder={this.onChangeContentOrder}
                downloadPdf={this.downloadPDF}
                pageRefs={this.pageRefs}
              />
            )}
          </div>
        </div>

        <DiscardComparisonModal show={showDiscardComparisonModal} onConfirm={this.onCloseComparison} onCancel={this.closeDiscardComparisonModal} />

        <StatusFeedbackModal onCancel={this.closeGeneratingProposalModal} show={showGeneratingProposalModal} status={proposalStatus} i18nBasePath="Proposal" />

        {showBrandingFormModal && (
          <DownloadProposalFormModal showModal={showBrandingFormModal} onCancel={this.hideDownloadForm}>
            {this.getDownloadForm()}
          </DownloadProposalFormModal>
        )}

        <WarningModal
          show={showCloseComparisonModal}
          onConfirm={this.onCloseComparison}
          onCancel={this.closeCloseComparisonModal}
          title={formatMessage('CloseComparison.Title')}
          body={<FormattedMessage tagName="p" id="CloseComparison.Body" />}
        />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState) => {
  const currentComparison = state.comparison.activeComparison;

  return {
    comparison: currentComparison,
    comparisonCorrelations: state.comparisonCorrelations,
    proposalItems: state.proposalV2.items,
    proposalItemsCorrelation: state.proposalV2.itemsCorrelation,
    proposalGifItems: state.proposalV2.gifItems,
    gifHideBaseScenario: state.proposalV2.gifHideBaseScenario,
    proposalDate: state.proposalV2.date,
    proposalContact: state.proposalV2.contact,
    riskProfile: state.proposalV2.riskProfile,
    toolbarOptions: state.proposalV2.toolbarOptions,
    proposalTemplateOptions: state.proposalV2.proposalTemplateOptions,
    templates: state.proposalV2.templates,
    locale: state.intl.locale,
    context: state.auth.context,
    rationaleAnswers: state.rationaleQuestionnaire.answers,
    questionnaires: state.kycQuestionnaire.questionnaires,
    omniRiskProfilesQuestionnaire: state.omniRiskProfiles.questionnaire,
    isEditVisible: state.comparison.portfolioEditor.isVisible,
    isEditPinned: state.comparison.portfolioEditor.isPinned,
    canPortfolioSave: !!state.editPortfolio.canSave,
    isSaving: state.comparison.isSaving,
    tab: state.comparison.tab,
    originPath: state.comparison.originPath,
    theme: state.auth.user?.preferences?.theme ?? ThemeMode.DEFAULT,
  };
};

export default withRouter(
  connect(mapStateToProps, {
    setProposalTemplate,
    setProposalItems,
    setProposalItemsCorrelation,
    setProposalGifItems,
    setProposalItemFlags,
    setComparisonItemFlags,
    toggleGifHideBaseScenario,
    setProposalTemplateLocale,
    setProposalTemplateTheme,
    setProposalTemplateHiddenLayouts,
    setProposalTemplateSections,
    setProposalTemplateDivisions,
    setProposalRiskProfile,
    calculateHistoricalScenarios,
    setToolbarOption,
    setProposalContact,
    setProposalDate,
    downloadProposalPDF,
    closeComparison,
    resetProposal,
    setProposalTemplateOptions,
    setProposalStyleOptions,
    setTextAreaData,
    setSelectedView,
    cancelPDFDownload,
    toggleShortHistory,
    consumeLimit,
    loadProposalTemplates,
    getKycQuestionnaires,
    loadOmniRiskProfilesQuestionnaire,
    savePortfolio,
    addPortfoliosToComparison,
    saveComparison,
    setComparisonName,
    setComparisonContact,
    changeItemId,
    rerunComparison,
    changeComparisonItemsOrder,
    changeProposalItemsOrder,
    changeProposalContentOrder,
    resetPortfolio,
    updateComparisonGroup,
    replacePortfolioInComparison,
    setProposalLocale,
  })(ProposalPage),
);
